/**
 * Настройки видов внутри свечей: кластеры, перекос, крупные объёмы.
 *
 * Три индикатора, одна форма настроек: слой рисует их одним кодом, и
 * разные формы разошлись бы при первой правке.
 */

import type { OrderFlowVenue } from '@specter/types';
import type { IndicatorParams } from '../indicators/types.js';

export type FlowBarsView = 'clusters' | 'imbalance' | 'large' | 'liquidations';

/** Что писать в клетке кластера. */
export type ClusterContent = 'bidask' | 'delta' | 'volume';

export interface FlowBarsSettings {
  readonly view: FlowBarsView;
  readonly venues: ReadonlySet<OrderFlowVenue>;
  readonly unit: 'usd' | 'coin';
  readonly content: ClusterContent;
  /** Подсвечивать перекос в клетках кластера. */
  readonly highlight: boolean;
  /** Главный уровень каждой свечи. */
  readonly poc: boolean;
  /** Во сколько раз одна сторона сильнее другой, чтобы считаться перекосом. */
  readonly ratio: number;
  /** Нижняя граница объёма стороны, долларов. */
  readonly minVolume: number;
  /** Сколько уровней перекоса подряд — стопка. */
  readonly stack: number;
  readonly singles: boolean;
  readonly zones: boolean;
  /** На сколько свечей вправо тянуть зону стопки. */
  readonly zoneBars: number;
  /** Во сколько раз уровень крупнее обычного, чтобы попасть на график. */
  readonly large: number;
  /** Ликвидации меньше этой суммы, долларов, не рисуются. */
  readonly minLiquidation: number;
  readonly colors: {
    readonly buy: string;
    readonly sell: string;
    readonly volume: string;
    readonly poc: string;
  };
}

function text(p: IndicatorParams, key: string, fallback: string): string {
  const value = p[key];
  return typeof value === 'string' && value !== '' ? value : fallback;
}

function number(p: IndicatorParams, key: string, fallback: number): number {
  const value = p[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function flag(p: IndicatorParams, key: string, fallback: boolean): boolean {
  const value = p[key];
  return typeof value === 'boolean' ? value : fallback;
}

export function barsSettings(p: IndicatorParams, view: FlowBarsView): FlowBarsSettings {
  const source = text(p, 'source', 'all');
  const content = text(p, 'content', 'bidask');
  return {
    view,
    venues: new Set<OrderFlowVenue>(
      source === 'binance' ? ['binance'] : source === 'bybit' ? ['bybit'] : ['binance', 'bybit'],
    ),
    unit: text(p, 'unit', 'usd') === 'coin' ? 'coin' : 'usd',
    content: content === 'delta' || content === 'volume' ? content : 'bidask',
    highlight: flag(p, 'highlight', true),
    poc: flag(p, 'poc', true),
    ratio: Math.max(1.1, number(p, 'ratio', 300) / 100),
    minVolume: Math.max(0, number(p, 'minVolume', 0)),
    stack: Math.max(2, Math.round(number(p, 'stack', 3))),
    singles: flag(p, 'singles', true),
    zones: flag(p, 'zones', true),
    zoneBars: Math.max(1, Math.round(number(p, 'zoneBars', 30))),
    large: Math.max(1.5, number(p, 'large', 3)),
    minLiquidation: Math.max(0, number(p, 'minLiquidation', 10_000)),
    colors: {
      buy: text(p, 'buyColor', '#16C784'),
      sell: text(p, 'sellColor', '#EA3943'),
      volume: text(p, 'volumeColor', '#7C8DB5'),
      poc: text(p, 'pocColor', '#F5A623'),
    },
  };
}

/** «52K», «1,2M», «840» — для клеток, где места на три-четыре знака. */
export function compact(value: number): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? '−' : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1).replace('.', ',')}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(abs >= 1e7 ? 0 : 1).replace('.', ',')}M`;
  if (abs >= 1e3) return `${sign}${Math.round(abs / 1e3)}K`;
  return `${sign}${Math.round(abs)}`;
}
